import React, { useState, useEffect } from 'react';
import { Star, MessageCircle, Search, Download, ChevronLeft, ChevronRight, Plus, Filter, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { STATUS_CONFIG, QUALIFICATION_CONFIG, SOURCE_CONFIG, formatDateShort, getSourceLabel } from '../utils/constants';
import WhatsAppModal from './WhatsAppModal';
import LeadDetail from './LeadDetail';
import ManualLeadModal from './ManualLeadModal';

export default function LeadTable({ leads, total, page, pages, filters, setFilters, onRefresh, showDatasetCol, datasets }) {
  const [waLead, setWaLead] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const [showManual, setShowManual] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ categories: [], cities: [], sources: [], services: [], utmCampaigns: [] });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api.get('/leads/meta/filters', { params: { dataset_id: filters.dataset_id } }).then(res => setFilterOptions(res.data)).catch(() => {});
  }, [filters.dataset_id]);

  const updateStatus = async (id, status) => { try { await api.put(`/leads/${id}/status`, { status }); onRefresh(); } catch { toast.error('Error'); } };
  const updateQualification = async (id, q) => { try { await api.put(`/leads/${id}/qualification`, { qualification: q }); onRefresh(); } catch { toast.error('Error'); } };
  const toggleFavorite = async (id) => { try { await api.put(`/leads/${id}/favorite`); onRefresh(); } catch { toast.error('Error'); } };
  const deleteLead = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name || 'Sin nombre'}"?`)) return;
    try { await api.delete(`/leads/${id}`); toast.success('Lead eliminado'); onRefresh(); } catch { toast.error('Error'); }
  };

  const exportCSV = () => {
    const token = localStorage.getItem('crm_token');
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => { if (v && k !== 'page' && k !== 'search') params.set(k, v); });
    fetch(`/api/leads/export/csv?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const a = document.createElement('a'); a.href = window.URL.createObjectURL(blob);
        a.download = `leads_${Date.now()}.csv`; document.body.appendChild(a); a.click(); a.remove();
        toast.success('CSV descargado');
      }).catch(() => toast.error('Error'));
  };

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 200px' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input placeholder="Buscar leads..." value={filters.search || ''} onChange={e => setFilters({ ...filters, search: e.target.value, page: 1 })} style={{ paddingLeft: 36 }} />
        </div>
        <button className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowFilters(!showFilters)}><Filter size={14} /> Filtros</button>
        <button className="btn btn-secondary btn-sm" onClick={exportCSV}><Download size={14} /> CSV</button>
        <button className="btn btn-primary btn-sm" onClick={() => setShowManual(true)}><Plus size={14} /> Agregar lead</button>
      </div>

      {showFilters && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, padding: 14, background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <select value={filters.status || ''} onChange={e => setFilters({...filters, status: e.target.value, page: 1})} style={{ width: 170 }}>
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filters.qualification || ''} onChange={e => setFilters({...filters, qualification: e.target.value, page: 1})} style={{ width: 150 }}>
            <option value="">Calificación</option>
            {Object.entries(QUALIFICATION_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
          <select value={filters.city || ''} onChange={e => setFilters({...filters, city: e.target.value, page: 1})} style={{ width: 160 }}>
            <option value="">Todas las ciudades</option>
            {filterOptions.cities?.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filters.category || ''} onChange={e => setFilters({...filters, category: e.target.value, page: 1})} style={{ width: 160 }}>
            <option value="">Todos los sectores</option>
            {filterOptions.categories?.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filters.source || ''} onChange={e => setFilters({...filters, source: e.target.value, page: 1})} style={{ width: 150 }}>
            <option value="">Todas las fuentes</option>
            {filterOptions.sources?.map(s => <option key={s} value={s}>{SOURCE_CONFIG[s]?.label || s}</option>)}
          </select>
          {filterOptions.utmCampaigns?.length > 0 && (
            <select value={filters.utm_campaign || ''} onChange={e => setFilters({...filters, utm_campaign: e.target.value, page: 1})} style={{ width: 170 }}>
              <option value="">Todas las campañas</option>
              {filterOptions.utmCampaigns.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ ...filters, status:'', qualification:'', city:'', category:'', source:'', service_interest:'', utm_campaign:'', page:1 })}>Limpiar</button>
        </div>
      )}

      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>{total} leads encontrados</div>

      <div className="table-container">
        <table>
          <thead><tr>
            <th style={{width:36}}></th><th>Negocio</th><th>Teléfono</th><th>Email</th>
            {showDatasetCol && <th>Base</th>}
            <th>Sector</th><th>Ciudad</th><th>Fuente</th><th>Campaña</th><th>Servicio</th><th>Landing</th>
            <th>Estado</th><th>Calificación</th><th>Ingreso</th><th>Último contacto</th><th style={{width:80}}></th>
          </tr></thead>
          <tbody>
            {leads.length === 0 ? (
              <tr><td colSpan={showDatasetCol ? 16 : 15} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No se encontraron leads</td></tr>
            ) : leads.map(lead => (
              <tr key={lead.id}>
                <td><button style={{ background:'none', border:'none', cursor:'pointer', padding:4 }} onClick={() => toggleFavorite(lead.id)}>
                  <Star size={15} fill={lead.is_favorite ? '#eab308' : 'none'} color={lead.is_favorite ? '#eab308' : 'var(--text-muted)'} />
                </button></td>
                <td><span style={{ fontWeight:600, cursor:'pointer', color:'var(--accent)' }} onClick={() => setDetailLead(lead.id)}>{lead.business_name || '(Sin nombre)'}</span></td>
                <td style={{ fontSize:13 }}>{lead.phone || '—'}</td>
                <td style={{ fontSize:13, color:'var(--text-secondary)' }}>{lead.email || '—'}</td>
                {showDatasetCol && <td style={{ fontSize:12, color:'var(--text-muted)' }}>{lead.dataset_name || '—'}</td>}
                <td style={{ fontSize:13 }}>{lead.category || '—'}</td>
                <td style={{ fontSize:13 }}>{lead.city || '—'}</td>
                <td><span className="badge" style={{ background: SOURCE_CONFIG[lead.source]?.bg || 'rgba(100,116,139,0.15)', color: SOURCE_CONFIG[lead.source]?.color || '#64748b', fontSize:11 }}>{getSourceLabel(lead)}</span></td>
                <td style={{ fontSize:12, color:'var(--text-muted)' }}>{lead.utm_campaign || '—'}</td>
                <td style={{ fontSize:12, color:'var(--text-muted)' }}>{lead.service_interest || '—'}</td>
                <td style={{ fontSize:12, color:'var(--text-muted)', maxWidth:100, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{lead.landing_page || '—'}</td>
                <td><select value={lead.status} onChange={e => updateStatus(lead.id, e.target.value)}
                  style={{ background: STATUS_CONFIG[lead.status]?.bg, color: STATUS_CONFIG[lead.status]?.color, border:'none', borderRadius:50, padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer', width:'auto' }}>
                  {Object.entries(STATUS_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                </select></td>
                <td><select value={lead.qualification} onChange={e => updateQualification(lead.id, e.target.value)}
                  style={{ background:'transparent', border:'none', fontSize:13, cursor:'pointer', color: QUALIFICATION_CONFIG[lead.qualification]?.color, fontWeight:600, width:'auto' }}>
                  {Object.entries(QUALIFICATION_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                </select></td>
                <td style={{ fontSize:12, color:'var(--text-muted)' }}>{formatDateShort(lead.created_at)}</td>
                <td><input type="date" value={lead.last_contacted_at ? lead.last_contacted_at.split('T')[0] : ''} onChange={async e => { try { await api.put('/leads/' + lead.id + '/last-contact', { date: e.target.value }); onRefresh(); } catch {} }} style={{ background:'transparent', border:'1px solid var(--border)', borderRadius:6, padding:'2px 6px', fontSize:11, color:'var(--text-secondary)', width:120, cursor:'pointer' }} /></td>
                <td><div style={{ display:'flex', gap:2 }}>
                  {lead.phone && <button className="btn btn-ghost" style={{ padding:4, borderRadius:6 }} onClick={() => setWaLead(lead)}><MessageCircle size={15} color="#22c55e" /></button>}
                  <button className="btn btn-ghost" style={{ padding:4, borderRadius:6 }} onClick={() => deleteLead(lead.id, lead.business_name)}><Trash2 size={15} color="var(--danger)" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, marginTop:16 }}>
          <button className="btn btn-ghost" disabled={page<=1} onClick={() => setFilters({...filters, page: page-1})}><ChevronLeft size={16}/></button>
          <span style={{ fontSize:13, color:'var(--text-secondary)' }}>Página {page} de {pages}</span>
          <button className="btn btn-ghost" disabled={page>=pages} onClick={() => setFilters({...filters, page: page+1})}><ChevronRight size={16}/></button>
        </div>
      )}

      {waLead && <WhatsAppModal lead={waLead} onClose={() => { setWaLead(null); onRefresh(); }} />}
      {detailLead && <LeadDetail leadId={detailLead} onClose={() => { setDetailLead(null); onRefresh(); }} />}
      {showManual && <ManualLeadModal datasets={datasets} defaultDatasetId={filters.dataset_id} onClose={() => { setShowManual(false); onRefresh(); }} />}
    </>
  );
}
